import { IssueFieldEnum } from "@mcapps/shared/enums";
import { IUserDepricated } from "@mctypes/global.types";
import { IIssueBoardApi } from "@mcapps/shared";
import { ISortColumn } from "@schemas/issueSchema";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import React from "react";
import { UserCellRenderer } from "../../../../../../components/ConnectedIssues/ConnectedIssues";
import { ISSUE_COLUMN_LABELS, ISSUE_SECTIONS } from "../../../issueConstants";
import { dateExportFormatter } from "../issueTableFormatters";

export type HideAndSort = (colId: string) => { hide: boolean; sort: "asc" | "desc" | null; type: string };

export const createHideAndSort =
  ({
    columnsVisibility,
    issueBoard,
    sortedCol,
  }: {
    columnsVisibility: string[];
    issueBoard: IIssueBoardApi;
    sortedCol: ISortColumn | null;
  }): HideAndSort =>
  (colId: string) => {
    return {
      hide: !columnsVisibility.includes(colId),
      sort: sortedCol?.column === colId ? (sortedCol.direction.toLowerCase() as "asc" | "desc") : null,
      type: issueBoard.disabledColumns?.includes(colId) ? "alwaysHidden" : "default",
    };
  };

export const createIssueColumnBaseDefGetter =
  (hideAndSort: HideAndSort) =>
  (dataField: IssueFieldEnum): ColDef<any> => ({
    field: dataField,
    headerName: ISSUE_COLUMN_LABELS[dataField],
    ...hideAndSort(dataField),
  });

export const createDateColumnEditor = () => (dateType: string) => ({
  editable: true,
  context: ISSUE_SECTIONS.DATES,
  cellEditor: "agDateCellEditor",
  valueFormatter: dateExportFormatter,
  valueGetter: (params) => {
    if (params.data.dates && params.data.dates[dateType]) {
      return new Date(params.data.dates[dateType]);
    }

    return null;
  },
});

export const createUserColumnEditor = (members: IProjectMemberApiItem[]) => (userType: string) =>
  ({
    editable: true,
    cellEditor: "agRichSelectCellEditor",
    cellEditorPopup: true,
    suppressPaste: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.data[userType]?.name?.length > 0 ? params.data[userType]?.name : " ",
    valueGetter: (params) => params.data[userType]?.name ?? null,
    cellEditorParams: {
      values: [
        null,
        ...members
          .map((member) => member.user)
          .filter((user): user is IUserDepricated => user != null && typeof user !== "string"),
      ],
      formatValue: (value) => value?.name ?? "",
      allowTyping: true,
      filterList: true,
      cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
    },
    filter: "agSetColumnFilter",
    sortable: true,
  }) as ColDef<any>;
